/**
 * Turns the store's feedback field into sound and vibration.
 *
 * Uses `useLive.subscribe` rather than a `useEffect` on the field, and that is
 * not a style choice: a single feedback slot read from an effect is lossy. If two
 * `set` calls land in one React batch — a `card-played` and its `trick-won`
 * arriving in the same frame on a slow phone — the effect only ever observes the
 * last value and the intermediate cue vanishes with nothing to debug. A
 * subscriber runs synchronously inside every `set`, so it cannot miss one.
 *
 * Comparison is by object identity, not by id: that survives `reset()` putting
 * the field back to `null` on unmount, and it needs no "last played" ref that
 * would itself go stale when the next game restarts the counter.
 */

import { useEffect } from 'react';
import { playCue } from '@/lib/feedback';
import { useLive } from '@/store/live';

export function useFeedbackBridge(): void {
  useEffect(
    () =>
      useLive.subscribe((next, previous) => {
        if (next.feedback && next.feedback !== previous.feedback) {
          playCue(next.feedback.cue);
        }
      }),
    []
  );
}
